import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronRight, Info, Loader2, SkipForward, Check, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useBilling } from "../context/BillingContext";
import PackPreviewSheet from "./PackPreviewSheet";

/**
 * Pass-the-phone gameplay flow.
 *
 * Stages:
 *   1. picking  — full-screen pack chooser (Pick your pack ✨)
 *   2. setup    — 2 players: name + avatar (Who's playing?)
 *   3. question — Neon question card + Skip / Next
 *   4. pass     — "Pass the phone" handoff card
 *
 * Pack data shape: pack.items.truth_or_dare = [{ truth, dare }, ...]
 * We pick one prompt per round (alternating truth/dare for variety).
 */

const PACK_CATALOG = [
  {
    id: "spicy-mode",
    name: "Spicy Vibes",
    icon: "🌶️",
    img: "/assets/home/pack-spicy.png",
    tag: "HOT",
    tagBg: "rgba(255, 46, 154, 0.15)",
    tagBorder: "#FF2E9A",
    tagText: "#FF2E9A",
    accent: "#FF2E9A",
    description: "Hot questions for bold nights.",
    spice: 3,
    spiceColor: "#FF2E9A",
    tier: "premium",
  },
  {
    id: "deep-talk",
    name: "Deep Talks",
    icon: "💜",
    img: "/assets/home/pack-deep.png",
    tag: "DEEP",
    tagBg: "rgba(155, 107, 255, 0.18)",
    tagBorder: "#9B6BFF",
    tagText: "#C9B7FF",
    accent: "#9B6BFF",
    description: "Get vulnerable. Build connection.",
    spice: 3,
    spiceColor: "#9B6BFF",
    tier: "premium",
  },
  {
    id: "we-listen",
    name: "Most Likely To",
    icon: "🤪",
    img: "/assets/home/pack-fun.png",
    tag: "FUN",
    tagBg: "rgba(255, 195, 0, 0.18)",
    tagBorder: "#FFC300",
    tagText: "#FFD45E",
    accent: "#FFB547",
    description: "Chaos activated. Who said that? 👀",
    spice: 3,
    spiceColor: "#FFD45E",
    tier: "free",
  },
  {
    id: "date-night",
    name: "Date Night",
    icon: "🍷",
    img: "/assets/home/pack-romantic.png",
    tag: "ROMANTIC",
    tagBg: "rgba(255, 107, 71, 0.18)",
    tagBorder: "#FF6B47",
    tagText: "#FF9F7A",
    accent: "#FF6B47",
    description: "Cute, flirty & fun. Perfect for you two.",
    spice: 3,
    spiceColor: "#FF6B47",
    tier: "free",
  },
  {
    id: "lowkey-toxic",
    name: "Red Flag Radar",
    icon: "👀",
    img: "/assets/home/pack-redflag.png",
    tag: "BOLD",
    tagBg: "rgba(255, 61, 90, 0.18)",
    tagBorder: "#FF3D5A",
    tagText: "#FF6B85",
    accent: "#FF3D5A",
    description: "Expose gently. Find out the truth.",
    spice: 3,
    spiceColor: "#FF3D5A",
    tier: "premium",
  },
  {
    id: "after-dark",
    name: "Drunk Questions",
    icon: "🍸",
    img: "/assets/home/pack-night.png",
    tag: "WILD",
    tagBg: "rgba(61, 214, 140, 0.18)",
    tagBorder: "#3DD68C",
    tagText: "#7DE6A9",
    accent: "#3DD68C",
    description: "No filters. No limits. Just chaos.",
    spice: 3,
    spiceColor: "#3DD68C",
    tier: "premium",
  },
];

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #FF6B85, #FF2E9A 60%, #9B6BFF)",
  "linear-gradient(135deg, #FFB547, #FF6B47 60%, #FF2E9A)",
];
const AVATAR_EMOJIS = ["👩", "👨"];

/* Cinematic avatar collection — 6 neon-rimmed portraits */
const AVATAR_COLLECTION = [
  { id: "pink-w",   img: "/assets/home/avatar-pink-w.png",   color: "#FF2E9A", name: "Magenta" },
  { id: "purple-w", img: "/assets/home/avatar-purple-w.png", color: "#9B6BFF", name: "Violet" },
  { id: "orange-w", img: "/assets/home/avatar-orange-w.png", color: "#FFB547", name: "Amber" },
  { id: "red-m",    img: "/assets/home/avatar-red-m.png",    color: "#FF3D5A", name: "Crimson" },
  { id: "blue-m",   img: "/assets/home/avatar-blue-m.png",   color: "#4FC3FF", name: "Cyan" },
  { id: "green-m",  img: "/assets/home/avatar-green-m.png",  color: "#3DD68C", name: "Jade" },
];
const DEFAULT_AVATARS = ["pink-w", "blue-m"];

/* ============================================================ */
/* Reusable: faint animated floating particles                   */
/* ============================================================ */
function FloatingParticles({ density = 12, color = "#FF2E9A", className = "" }) {
  const particles = useMemo(() => {
    return new Array(density).fill(0).map((_, i) => {
      const left = Math.random() * 100;
      const size = 1.5 + Math.random() * 2.5;
      const delay = Math.random() * 8;
      const duration = 9 + Math.random() * 7;
      const opacity = 0.25 + Math.random() * 0.45;
      return { id: i, left, size, delay, duration, opacity };
    });
  }, [density]);
  return (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
      aria-hidden
    >
      {particles.map((p) => (
        <span
          key={p.id}
          className="vc-particle"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            background: color,
            boxShadow: `0 0 ${p.size * 3}px ${color}`,
            opacity: p.opacity,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes vcFloatUp {
          0%   { transform: translateY(110vh) translateX(0); opacity: 0; }
          15%  { opacity: var(--peak-opacity, 0.7); }
          50%  { transform: translateY(50vh) translateX(8px); }
          85%  { opacity: var(--peak-opacity, 0.7); }
          100% { transform: translateY(-12vh) translateX(-6px); opacity: 0; }
        }
        .vc-particle {
          position: absolute;
          top: 0;
          border-radius: 50%;
          animation-name: vcFloatUp;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
        }
      `}</style>
    </div>
  );
}

export default function PlayTogetherGame({ packId: initialPackId, onExit }) {
  const { isPremium, isPackUnlocked, openPaywall } = useBilling();
  const [packId, setPackId] = useState(initialPackId || null);
  const [pack, setPack] = useState(null);
  const [stage, setStage] = useState(initialPackId ? "setup" : "picking");
  const [players, setPlayers] = useState([
    { name: "", avatarId: DEFAULT_AVATARS[0] },
    { name: "", avatarId: DEFAULT_AVATARS[1] },
  ]);
  const [activeAvatarSlot, setActiveAvatarSlot] = useState(0); // 0 or 1 — which player slot the avatar picker is editing
  const [active, setActive] = useState(0);
  const [round, setRound] = useState(1);
  const [streak, setStreak] = useState(0);
  const [usedIdx, setUsedIdx] = useState(new Set());
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [previewing, setPreviewing] = useState(null);  // pack meta of locked pack the user tapped

  // Fetch pack content whenever a pack is chosen
  useEffect(() => {
    if (!packId) return;
    api.getPack(packId).then(setPack).catch(() => toast.error("Couldn't load pack"));
  }, [packId]);

  const promptPool = useMemo(() => pack?.items?.truth_or_dare || [], [pack]);
  const totalQuestions = promptPool.length || 50;
  const packMeta = useMemo(
    () => PACK_CATALOG.find((p) => p.id === packId) || PACK_CATALOG[0],
    [packId]
  );

  const pickPrompt = () => {
    if (!promptPool.length) return null;
    let tries = 0;
    let idx = -1;
    while (tries++ < 80) {
      idx = Math.floor(Math.random() * promptPool.length);
      if (!usedIdx.has(idx)) break;
    }
    if (idx < 0) idx = Math.floor(Math.random() * promptPool.length);
    setUsedIdx((s) => new Set(s).add(idx));
    const item = promptPool[idx];
    // Alternate truth & dare for variety; default to truth
    const useDare = Math.random() < 0.45 && item.dare;
    return { text: useDare ? item.dare : item.truth, kind: useDare ? "dare" : "truth" };
  };

  const handlePickPack = (id) => {
    const meta = PACK_CATALOG.find((p) => p.id === id);
    if (!meta) return;
    const locked = meta.tier === "premium" && !isPremium && !isPackUnlocked(id);
    if (locked) {
      setPreviewing(meta);
      return;
    }
    setPackId(id);
    setStage("setup");
  };

  const handleContinueFromPicker = () => {
    if (!packId) {
      toast.error("Pick a pack to continue");
      return;
    }
    setStage("setup");
  };

  const handleStartGame = () => {
    const ok = players.every((p) => p.name.trim().length > 0);
    if (!ok) {
      toast.error("Add both names");
      return;
    }
    if (!promptPool.length) {
      toast.error("This pack is empty");
      return;
    }
    const first = pickPrompt();
    if (!first) return;
    setCurrentPrompt(first);
    setRound(1);
    setActive(0);
    setStreak(0);
    setStage("question");
    // Fire-and-forget streak ping — idempotent per day.
    api.streakPing().catch(() => { /* offline tolerant */ });
  };

  const handleSkip = () => {
    const next = pickPrompt();
    if (!next) return;
    setCurrentPrompt(next);
    setStreak(0);
  };

  const handleNextQuestion = () => {
    setStreak((s) => s + 1);
    setStage("pass");
  };

  const handleReadyAfterPass = () => {
    const next = pickPrompt();
    if (!next) return;
    setCurrentPrompt(next);
    setActive((a) => (a + 1) % 2);
    setRound((r) => r + 1);
    setStage("question");
  };

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="together-game-screen">
      {/* warm pink bloom */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          top: 60,
          left: "50%",
          transform: "translateX(-50%)",
          width: 540,
          height: 540,
          background:
            "radial-gradient(circle, rgba(255, 46, 154, 0.42) 0%, rgba(255, 107, 71, 0.18) 35%, transparent 70%)",
          filter: "blur(48px)",
          zIndex: 0,
        }}
        aria-hidden
      />

      <AnimatePresence mode="wait">
        {stage === "picking" && (
          <PickPackView
            key="picking"
            packId={packId}
            onBack={onExit}
            onPick={handlePickPack}
          />
        )}
        {stage === "setup" && (
          <SetupView
            key="setup"
            players={players}
            setPlayers={setPlayers}
            activeAvatarSlot={activeAvatarSlot}
            setActiveAvatarSlot={setActiveAvatarSlot}
            onBack={() => setStage("picking")}
            onStart={handleStartGame}
            packReady={!!pack}
            accent={packMeta.accent}
          />
        )}
        {stage === "question" && (
          <QuestionView
            key="question"
            pack={pack}
            packMeta={packMeta}
            prompt={currentPrompt}
            round={round}
            total={totalQuestions}
            streak={streak}
            playerName={players[active]?.name}
            playerIndex={active}
            playerAvatarId={players[active]?.avatarId}
            onBack={() => setStage("setup")}
            onSkip={handleSkip}
            onNext={handleNextQuestion}
          />
        )}
        {stage === "pass" && (
          <PassView
            key="pass"
            packMeta={packMeta}
            round={round}
            total={totalQuestions}
            streak={streak}
            nextName={players[(active + 1) % 2]?.name}
            nextAvatarId={players[(active + 1) % 2]?.avatarId}
            onReady={handleReadyAfterPass}
            onBack={() => setStage("question")}
          />
        )}
      </AnimatePresence>

      <PackPreviewSheet
        pack={previewing}
        onClose={() => setPreviewing(null)}
        onUnlock={() => {
          const id = previewing?.id;
          setPreviewing(null);
          openPaywall("pack_locked", id);
        }}
      />
    </div>
  );
}

/* ============================================================ */
/* PICK PACK VIEW (slide 2)                                      */
/* ============================================================ */
function PickPackView({ packId, onBack, onPick }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -14 }}
      transition={{ duration: 0.35 }}
      className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col"
      data-testid="together-pickpack-screen"
    >
      <FloatingParticles density={14} color="#FF2E9A" />

      {/* Top bar */}
      <div className="flex items-center justify-between mb-3 relative z-[2]">
        <button
          onClick={onBack}
          aria-label="Back"
          data-testid="together-pickpack-back"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10" aria-hidden />
      </div>

      {/* Title */}
      <div className="text-center mb-6 mt-1 relative z-[2]">
        <h1
          className="text-white font-extrabold tracking-tight inline-flex items-center gap-2"
          style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: "clamp(1.7rem, 6.4vw, 2.1rem)",
            letterSpacing: "-0.025em",
            // Crisp neon — no blurred halo on text itself
            textShadow: "0 1px 0 rgba(0,0,0,0.55)",
          }}
          data-testid="together-pickpack-title"
        >
          Pick your pack
          <motion.span
            animate={{ rotate: [0, 8, -8, 0], scale: [1, 1.12, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            style={{ filter: "drop-shadow(0 0 6px rgba(255, 46, 154, 0.7))" }}
          >
            ✨
          </motion.span>
        </h1>
        <p className="text-[#B8B8CC] mt-2" style={{ fontSize: "13.5px" }}>
          Tap to start. Premium packs preview before unlock.
        </p>
      </div>

      {/* Pack cards */}
      <div className="space-y-3 mb-6 relative z-[2]" data-testid="together-pickpack-list">
        {PACK_CATALOG.map((p, idx) => {
          const sel = packId === p.id;
          return (
            <motion.button
              key={p.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.05 + idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => onPick(p.id)}
              data-testid={`together-pickpack-card-${p.id}`}
              whileTap={{ scale: 0.985 }}
              className="w-full rounded-2xl p-3.5 flex items-center gap-3 text-left transition relative overflow-hidden"
              style={{
                background: `linear-gradient(180deg, ${p.accent}1f 0%, rgba(10, 6, 10, 0.78) 100%)`,
                border: `1.5px solid ${sel ? p.accent : `${p.accent}55`}`,
                boxShadow: sel
                  ? `0 0 26px ${p.accent}b0, inset 0 0 22px ${p.accent}26, inset 0 1px 0 rgba(255,255,255,0.08)`
                  : `0 0 12px ${p.accent}3a, inset 0 0 18px ${p.accent}14, inset 0 1px 0 rgba(255,255,255,0.06)`,
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
              }}
            >
              {/* Inner radial glow behind the icon */}
              <div
                className="absolute pointer-events-none"
                style={{
                  left: -16, top: -16, width: 120, height: 120,
                  background: `radial-gradient(circle, ${p.accent}40 0%, transparent 70%)`,
                  filter: "blur(18px)",
                }}
                aria-hidden
              />

              <div
                className="relative flex-shrink-0 w-[68px] h-[68px] rounded-2xl overflow-hidden flex items-center justify-center"
                style={{
                  background: `radial-gradient(circle, ${p.accent}33 0%, rgba(8, 4, 12, 0.85) 75%)`,
                  border: `1px solid ${p.accent}66`,
                  boxShadow: `inset 0 0 14px ${p.accent}30, 0 0 14px ${p.accent}80`,
                }}
              >
                <img
                  src={p.img}
                  alt=""
                  className="w-[60px] h-[60px] object-contain"
                  style={{ filter: `drop-shadow(0 0 10px ${p.accent})` }}
                />
              </div>

              <div className="flex-1 min-w-0 relative">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="text-white text-[15px] font-extrabold leading-tight" style={{ letterSpacing: "-0.01em" }}>
                    {p.name}
                  </h3>
                  <span
                    className="text-[9.5px] font-black tracking-[0.16em] px-2 py-[3px] rounded-md"
                    style={{
                      background: p.tagBg,
                      color: p.tagText,
                      border: `1px solid ${p.tagBorder}`,
                      boxShadow: `0 0 10px ${p.tagBorder}66, inset 0 0 6px ${p.tagBorder}1f`,
                    }}
                  >
                    {p.tag}
                  </span>
                  <span
                    className="inline-flex items-center text-[9px] font-black tracking-[0.1em] px-2 py-[2px] rounded-full uppercase"
                    style={
                      p.tier === "premium"
                        ? {
                            background: "linear-gradient(135deg, #FF2E9A, #FF6B47)",
                            color: "#FFFFFF",
                            border: "1px solid rgba(255, 255, 255, 0.32)",
                            boxShadow: "0 0 10px rgba(255, 46, 154, 0.6)",
                          }
                        : {
                            background: "rgba(61, 214, 140, 0.16)",
                            color: "#7DE6A9",
                            border: "1px solid rgba(61, 214, 140, 0.6)",
                            boxShadow: "0 0 6px rgba(61, 214, 140, 0.4)",
                          }
                    }
                    data-testid={`together-pickpack-tier-${p.id}`}
                  >
                    {p.tier === "premium" ? "💎 Premium" : "✓ Free"}
                  </span>
                </div>
                <p className="text-[#B0AABB] text-[11.5px] leading-snug">{p.description}</p>
              </div>

              <div className="flex flex-col items-end gap-1.5 flex-shrink-0 relative">
                <div className="flex gap-1">
                  {Array(p.spice).fill(0).map((_, i) => (
                    <span
                      key={i}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: p.spiceColor, boxShadow: `0 0 6px ${p.spiceColor}` }}
                    />
                  ))}
                </div>
                <ChevronRight className="w-4 h-4 text-white/55" />
              </div>
            </motion.button>
          );
        })}
      </div>

      <div className="flex-1" />
    </motion.section>
  );
}

/* ============================================================ */
/* SETUP VIEW (slide 3) — 2 players + avatar picker              */
/* ============================================================ */
function SetupView({ players, setPlayers, activeAvatarSlot, setActiveAvatarSlot, onBack, onStart, packReady, accent }) {
  const updatePlayer = (idx, patch) => {
    setPlayers((curr) => curr.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };
  const otherSlotAvatar = players[1 - activeAvatarSlot]?.avatarId;
  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -14 }}
      transition={{ duration: 0.35 }}
      className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col"
      data-testid="together-setup-screen"
    >
      <FloatingParticles density={12} color="#FF2E9A" />

      {/* Top bar */}
      <div className="flex items-center justify-between mb-2 relative z-[2]">
        <button
          onClick={onBack}
          aria-label="Back"
          data-testid="together-setup-back"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10" aria-hidden />
      </div>

      {/* Title */}
      <div className="text-center mb-7 mt-2 relative z-[2]">
        <motion.div
          animate={{ scale: [1, 1.18, 1] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="inline-block text-[20px] mb-1"
          style={{ filter: "drop-shadow(0 0 10px #FF2E9A)" }}
        >
          💗
        </motion.div>
        <h1
          className="font-extrabold leading-[0.95] tracking-tight block"
          style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: "clamp(2.4rem, 10vw, 3rem)",
            letterSpacing: "-0.035em",
            background: "linear-gradient(90deg, #FFE5F0 0%, #FFC2E0 50%, #FF8FC8 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            filter: "drop-shadow(0 0 22px rgba(255, 46, 154, 0.85))",
          }}
          data-testid="together-setup-title"
        >
          Who's playing?
        </h1>
        <p className="text-white/65 mt-2.5 font-medium" style={{ fontSize: "14px" }}>
          Set the vibe.
        </p>
      </div>

      {/* Player 1 */}
      <PlayerRow
        index={0}
        label="Player 1"
        placeholder="Your name"
        player={players[0]}
        onChangeName={(v) => updatePlayer(0, { name: v })}
        onTapAvatar={() => setActiveAvatarSlot(0)}
        isPicking={activeAvatarSlot === 0}
        accent={accent}
      />

      {/* Divider with heart */}
      <div className="flex items-center gap-3 my-7 relative z-[2]">
        <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(255, 46, 154, 0.6), transparent)" }} />
        <motion.span
          animate={{ scale: [1, 1.18, 1], rotate: [-4, 4, -4] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="text-[24px]"
          style={{ filter: "drop-shadow(0 0 10px #FF2E9A)" }}
        >
          💗
        </motion.span>
        <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(255, 46, 154, 0.6), transparent)" }} />
      </div>

      {/* Player 2 */}
      <PlayerRow
        index={1}
        label="Player 2"
        placeholder="Their name"
        player={players[1]}
        onChangeName={(v) => updatePlayer(1, { name: v })}
        onTapAvatar={() => setActiveAvatarSlot(1)}
        isPicking={activeAvatarSlot === 1}
        accent={accent}
      />

      {/* Let's go CTA — sits between players and avatar picker */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        animate={packReady ? { boxShadow: [
          "0 0 30px rgba(255, 46, 154, 0.65), 0 0 70px rgba(255, 46, 154, 0.3), 0 8px 32px rgba(255, 46, 154, 0.4)",
          "0 0 48px rgba(255, 46, 154, 1), 0 0 110px rgba(255, 46, 154, 0.5), 0 10px 40px rgba(255, 46, 154, 0.55)",
          "0 0 30px rgba(255, 46, 154, 0.65), 0 0 70px rgba(255, 46, 154, 0.3), 0 8px 32px rgba(255, 46, 154, 0.4)",
        ] } : {}}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        onClick={onStart}
        disabled={!packReady}
        data-testid="together-setup-start"
        className="relative z-[2] w-full rounded-full py-4 mt-8 font-extrabold text-white flex items-center justify-center gap-3 transition disabled:opacity-50"
        style={{
          background: "linear-gradient(180deg, #FF4FBF 0%, #FF2E9A 60%, #C0288F 100%)",
          border: "1.5px solid rgba(255, 255, 255, 0.32)",
          fontSize: "18px",
        }}
      >
        {packReady ? (
          <>
            Let's go
            <ArrowRight className="w-5 h-5" strokeWidth={3.2} />
          </>
        ) : (
          <>
            <Loader2 className="w-5 h-5 animate-spin" /> Loading pack…
          </>
        )}
      </motion.button>

      {/* Avatar collection picker — shared horizontal scroll */}
      <div className="mt-10 mb-2 relative z-[2]" data-testid="together-setup-avatar-picker">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="flex-1 h-px max-w-[60px]" style={{ background: "linear-gradient(90deg, transparent, rgba(255, 46, 154, 0.5))" }} />
          <h3 className="text-white text-[14px] font-extrabold tracking-tight inline-flex items-center gap-1.5" style={{ letterSpacing: "-0.01em" }}>
            Choose your vibe
            <motion.span
              animate={{ scale: [1, 1.18, 1], rotate: [0, 8, -8, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              style={{ filter: "drop-shadow(0 0 6px #FFB547)" }}
            >
              ✨
            </motion.span>
          </h3>
          <div className="flex-1 h-px max-w-[60px]" style={{ background: "linear-gradient(90deg, rgba(255, 46, 154, 0.5), transparent)" }} />
        </div>
        <div className="text-center text-white/55 text-[11px] font-bold mb-3" data-testid="together-setup-avatar-target">
          for <span className="text-white">Player {activeAvatarSlot + 1}</span>
        </div>

        <div className="snap-carousel flex gap-3 -mx-5 px-5 pb-2 justify-center" data-testid="together-setup-avatar-list">
          {AVATAR_COLLECTION.map((av) => {
            const selectedHere = players[activeAvatarSlot]?.avatarId === av.id;
            const usedByOther = av.id === otherSlotAvatar;
            return (
              <button
                key={av.id}
                onClick={() => {
                  if (usedByOther) {
                    const myCurrent = players[activeAvatarSlot].avatarId;
                    setPlayers((curr) =>
                      curr.map((p, i) =>
                        i === activeAvatarSlot ? { ...p, avatarId: av.id } : { ...p, avatarId: myCurrent }
                      )
                    );
                  } else {
                    updatePlayer(activeAvatarSlot, { avatarId: av.id });
                  }
                }}
                data-testid={`together-setup-avatar-${av.id}`}
                className="flex-shrink-0 relative transition active:scale-95"
                style={{ width: 64, height: 64 }}
              >
                {selectedHere && (
                  <motion.div
                    animate={{ scale: [1, 1.18, 1], opacity: [0.6, 0.95, 0.6] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute inset-[-8px] rounded-full pointer-events-none"
                    style={{
                      background: `radial-gradient(circle, ${av.color}80 0%, transparent 70%)`,
                      filter: "blur(8px)",
                    }}
                    aria-hidden
                  />
                )}
                <div
                  className="relative w-full h-full rounded-full overflow-hidden"
                  style={{
                    border: `2.5px solid ${selectedHere ? av.color : `${av.color}66`}`,
                    boxShadow: selectedHere
                      ? `0 0 22px ${av.color}cc, inset 0 0 12px ${av.color}33`
                      : `0 0 10px ${av.color}66`,
                    background: "#0a060f",
                  }}
                >
                  <img src={av.img} alt={av.name} className="w-full h-full object-cover" />
                  {usedByOther && (
                    <div
                      className="absolute inset-0 flex items-end justify-center pb-1"
                      style={{ background: "linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.7) 100%)" }}
                    >
                      <span className="text-white text-[8.5px] font-black tracking-[0.1em] uppercase">P{2 - activeAvatarSlot}</span>
                    </div>
                  )}
                  {selectedHere && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 400 }}
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ background: av.color, boxShadow: `0 0 10px ${av.color}, 0 0 4px #fff` }}
                    >
                      <Check className="w-3 h-3 text-white" strokeWidth={4} />
                    </motion.div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-center gap-1.5 mt-5 text-white/55 text-[11.5px] font-medium">
          <Users className="w-3.5 h-3.5" style={{ color: "#FF2E9A" }} />
          Avatars will be shown to other players
        </div>
      </div>

      <div className="flex-1 min-h-[8px]" />
    </motion.section>
  );
}

function PlayerRow({ index, label, placeholder, player, onChangeName, onTapAvatar, isPicking, accent = "#FF2E9A" }) {
  const av = AVATAR_COLLECTION.find((a) => a.id === player.avatarId) || AVATAR_COLLECTION[0];
  return (
    <div className="flex items-center gap-4 relative z-[2]" data-testid={`together-setup-player-${index}`}>
      <button
        onClick={onTapAvatar}
        data-testid={`together-setup-avatar-tap-${index}`}
        className="relative flex-shrink-0 group active:scale-95 transition"
        aria-label={`Pick avatar for ${label}`}
      >
        {/* Pulsing halo when this slot is the active target */}
        {isPicking && (
          <motion.div
            animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.9, 0.5] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-[-10px] rounded-full pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${av.color}aa 0%, transparent 70%)`,
              filter: "blur(8px)",
            }}
            aria-hidden
          />
        )}
        <motion.div
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 3 + index * 0.4, repeat: Infinity, ease: "easeInOut", delay: index * 0.6 }}
          className="relative w-[78px] h-[78px] rounded-full overflow-hidden"
          style={{
            border: `2.5px solid ${av.color}`,
            boxShadow: `0 0 22px ${av.color}c0`,
            background: "#0a060f",
          }}
        >
          <img src={av.img} alt="" className="w-full h-full object-cover" />
        </motion.div>
      </button>

      <div className="flex-1 min-w-0">
        <div className="text-white/80 text-[12.5px] font-bold mb-1.5" style={{ letterSpacing: "0.01em" }}>
          {label}
        </div>
        <input
          value={player.name}
          onChange={(e) => onChangeName(e.target.value.slice(0, 20))}
          placeholder={placeholder}
          data-testid={`together-setup-input-${index}`}
          className="w-full bg-transparent outline-none text-white text-[15px] font-medium placeholder-white/35 py-2.5 px-3.5 rounded-xl"
          style={{
            background: "rgba(20, 14, 26, 0.6)",
            border: `1px solid ${accent}66`,
            boxShadow: `inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 0 14px ${accent}22`,
            backdropFilter: "blur(8px)",
          }}
        />
      </div>
    </div>
  );
}
/* ============================================================ */
/* QUESTION VIEW (slide 4 / 6)                                   */
/* ============================================================ */
function QuestionView({ pack, packMeta, prompt, round, total, streak, playerName, playerIndex, playerAvatarId, onBack, onSkip, onNext }) {
  if (!pack) {
    return (
      <div className="vc-bg min-h-screen flex items-center justify-center" data-testid="together-game-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF2E9A]" />
      </div>
    );
  }
  const accent = packMeta.accent || "#FF2E9A";
  return (
    <motion.section
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.35 }}
      className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-8 min-h-screen flex flex-col"
      data-testid="together-question-screen"
    >
      <FloatingParticles density={16} color={accent} />

      {/* Top bar */}
      <div className="flex items-center justify-between mb-2 relative z-[2]">
        <button
          onClick={onBack}
          aria-label="Back"
          data-testid="together-question-back"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2
          className="text-white text-[15px] font-extrabold tracking-tight inline-flex items-center gap-1.5"
          data-testid="together-question-pack-label"
          style={{ filter: `drop-shadow(0 0 10px ${accent}88)` }}
        >
          {packMeta.name} <span>{packMeta.icon}</span>
        </h2>
        <button
          aria-label="Info"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/55 hover:text-white transition active:scale-95"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
        >
          <Info className="w-4 h-4" />
        </button>
      </div>

      {/* Progress + streak */}
      <div className="flex items-center justify-between mb-4 px-1 relative z-[2]">
        <div className="flex items-center gap-2.5">
          <span className="text-white text-[12px] font-bold tabular-nums" data-testid="together-question-progress">
            {round}/{total}
          </span>
          <div className="w-[120px] h-[3px] rounded-full overflow-hidden" style={{ background: "rgba(255, 255, 255, 0.08)" }}>
            <motion.div
              className="h-full rounded-full"
              initial={false}
              animate={{ width: `${Math.min(100, (round / total) * 100)}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              style={{
                background: `linear-gradient(90deg, ${accent}, #FF4FBF)`,
                boxShadow: `0 0 8px ${accent}cc`,
              }}
            />
          </div>
        </div>
        <div className="flex items-center gap-1 text-white text-[12px] font-bold" data-testid="together-question-streak">
          <motion.span
            animate={streak > 0 ? { scale: [1, 1.25, 1] } : {}}
            transition={{ duration: 0.5 }}
            key={streak}
            style={{ filter: "drop-shadow(0 0 5px #FF6B47)" }}
          >
            🔥
          </motion.span>
          Streak: {streak}
        </div>
      </div>

      {/* "Your time" pill with animated avatar transition */}
      <div className="flex justify-center mb-3 relative z-[2]">
        {(() => {
          const av = AVATAR_COLLECTION.find((a) => a.id === playerAvatarId) || AVATAR_COLLECTION[playerIndex] || AVATAR_COLLECTION[0];
          return (
            <div
              className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full"
              style={{
                background: "rgba(20, 14, 26, 0.55)",
                border: `1px solid ${av.color}88`,
                boxShadow: `0 0 16px ${av.color}55, inset 0 1px 0 rgba(255, 255, 255, 0.06)`,
                backdropFilter: "blur(10px)",
              }}
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={`${playerIndex}-${playerAvatarId}`}
                  initial={{ rotateY: -90, opacity: 0, scale: 0.6 }}
                  animate={{ rotateY: 0, opacity: 1, scale: 1 }}
                  exit={{ rotateY: 90, opacity: 0, scale: 0.6 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="w-7 h-7 rounded-full overflow-hidden"
                  style={{
                    border: `1.5px solid ${av.color}`,
                    boxShadow: `0 0 10px ${av.color}aa`,
                  }}
                >
                  <img src={av.img} alt="" className="w-full h-full object-cover" />
                </motion.span>
              </AnimatePresence>
              <span className="text-white text-[12px] font-extrabold tracking-wide inline-flex items-center gap-1.5">
                Your time
                <span style={{ filter: "drop-shadow(0 0 4px #FFB547)" }}>🍷</span>
              </span>
            </div>
          );
        })()}
      </div>

      {/* Big neon question card — enlarged, glass-blurred, with inner glow */}
      <div className="flex-1 flex items-center justify-center my-2 relative z-[2]">
        <motion.div
          key={prompt?.text}
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className={`vc-q-card vc-q-${pack?.tone || "spicy"} vc-q-active w-full relative overflow-hidden`}
          data-testid="together-question-card"
        >
          <p
            className="vc-q-text relative"
            data-testid="together-question-text"
          >
            {prompt?.text || "Loading…"}
          </p>
          <motion.div
            animate={{ scale: [1, 1.08, 1], rotate: [-3, 3, -3] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="text-[42px] vc-q-gap-text-to-visual relative text-center"
            style={{ filter: `drop-shadow(0 0 12px ${accent}99)` }}
          >
            {packMeta.icon}
          </motion.div>
        </motion.div>
      </div>

      {/* Skip / Next row */}
      <div className="grid grid-cols-2 gap-3 mt-5 relative z-[2]">
        <button
          onClick={onSkip}
          data-testid="together-question-skip"
          className="rounded-full py-3.5 font-extrabold text-white/85 flex items-center justify-center gap-1.5 active:scale-[0.97] transition"
          style={{
            background: "rgba(20, 14, 26, 0.6)",
            border: "1.2px solid rgba(255, 255, 255, 0.14)",
            fontSize: "14px",
            backdropFilter: "blur(10px)",
          }}
        >
          <SkipForward className="w-4 h-4" />
          Skip
        </button>
        <motion.button
          onClick={onNext}
          data-testid="together-question-next"
          whileTap={{ scale: 0.97 }}
          animate={{ boxShadow: [
            `0 0 22px ${accent}88, 0 6px 22px ${accent}55`,
            `0 0 34px ${accent}cc, 0 6px 28px ${accent}80`,
            `0 0 22px ${accent}88, 0 6px 22px ${accent}55`,
          ] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="rounded-full py-3.5 font-extrabold text-white flex items-center justify-center gap-1.5 transition"
          style={{
            background: `linear-gradient(90deg, ${accent} 0%, #FF4FBF 100%)`,
            fontSize: "14px",
          }}
        >
          Next question
          <ChevronRight className="w-4 h-4" strokeWidth={3} />
        </motion.button>
      </div>
    </motion.section>
  );
}

/* ============================================================ */
/* PASS VIEW (slide 5) — tap anywhere to advance                  */
/* ============================================================ */
function PassView({ packMeta, round, total, streak, nextName, nextAvatarId, onReady, onBack }) {
  const accent = packMeta.accent || "#FF2E9A";
  const nextAv = AVATAR_COLLECTION.find((a) => a.id === nextAvatarId) || AVATAR_COLLECTION[0];
  return (
    <motion.section
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-10 min-h-screen flex flex-col cursor-pointer select-none"
      onClick={onReady}
      data-testid="together-pass-screen"
    >
      <FloatingParticles density={14} color={accent} />

      {/* Pack header (matches Question card so it feels continuous) */}
      <div
        className="flex items-center justify-between mb-2 relative z-[2]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onBack}
          aria-label="Back"
          data-testid="together-pass-back"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2
          className="text-white text-[15px] font-extrabold tracking-tight inline-flex items-center gap-1.5"
          style={{ filter: `drop-shadow(0 0 10px ${accent}88)` }}
        >
          {packMeta.name} <span>{packMeta.icon}</span>
        </h2>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/55"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
        >
          <Info className="w-4 h-4" />
        </div>
      </div>

      <div className="flex items-center justify-between mb-3 px-1 relative z-[2]">
        <div className="flex items-center gap-2.5">
          <span className="text-white text-[12px] font-bold tabular-nums">
            {round}/{total}
          </span>
          <div className="w-[120px] h-[3px] rounded-full overflow-hidden" style={{ background: "rgba(255, 255, 255, 0.08)" }}>
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min(100, (round / total) * 100)}%`,
                background: `linear-gradient(90deg, ${accent}, #FF4FBF)`,
                boxShadow: `0 0 8px ${accent}cc`,
              }}
            />
          </div>
        </div>
        <div className="flex items-center gap-1 text-white text-[12px] font-bold">
          <span style={{ filter: "drop-shadow(0 0 5px #FF6B47)" }}>🔥</span>
          Streak: {streak}
        </div>
      </div>

      {/* Cinematic neon pass-phone illustration */}
      <div className="flex-1 flex flex-col items-center justify-center text-center relative z-[2]">
        <motion.div
          animate={{ y: [0, -8, 0], rotate: [-2, 2, -2] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
          className="relative w-[320px] max-w-full mb-2"
        >
          <img
            src="/assets/home/pass-phone-cinematic.png"
            alt=""
            className="w-full object-contain"
            style={{
              filter: `drop-shadow(0 0 28px ${accent}aa) drop-shadow(0 0 60px ${accent}55)`,
            }}
          />
        </motion.div>

        <h2
          className="font-extrabold leading-[0.95] tracking-tight mb-3"
          style={{
            fontFamily: "'Sora', 'Inter', sans-serif",
            fontSize: "clamp(2.4rem, 11vw, 3.1rem)",
            letterSpacing: "-0.035em",
            background: "linear-gradient(90deg, #FFE5F0 0%, #FF8FC8 45%, #FF2E9A 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            textShadow: "0 0 1px rgba(255, 46, 154, 0.6)",
            filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
          }}
          data-testid="together-pass-title"
        >
          Pass the phone
        </h2>

        <motion.div
          animate={{ scale: [1, 1.14, 1] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="text-[26px] mb-6"
          style={{ filter: `drop-shadow(0 0 12px ${accent})` }}
        >
          💗
        </motion.div>

        {/* "Your turn" pill replacing the previous CTA — shows next player avatar */}
        <div
          className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full"
          style={{
            background: "rgba(20, 14, 26, 0.55)",
            border: `1.5px solid ${nextAv.color}cc`,
            boxShadow: `0 0 18px ${nextAv.color}77, inset 0 1px 0 rgba(255, 255, 255, 0.08)`,
            backdropFilter: "blur(10px)",
          }}
          data-testid="together-pass-turn-indicator"
        >
          <div
            className="w-7 h-7 rounded-full overflow-hidden"
            style={{ border: `1.5px solid ${nextAv.color}`, boxShadow: `0 0 10px ${nextAv.color}` }}
          >
            <img src={nextAv.img} alt="" className="w-full h-full object-cover" />
          </div>
          <span className="text-white text-[13px] font-extrabold tracking-wide">
            {nextName ? `${nextName}'s` : "Your"} turn
          </span>
        </div>

        <p className="text-white/55 text-[12px] font-medium mt-5 tracking-wide" data-testid="together-pass-tap-hint">
          Tap anywhere to continue
        </p>
      </div>
    </motion.section>
  );
}


